// ============================================================================
// AccueilTeamKpis — bande KPI discrete onglet Mon equipe (TEAM-KPI-WEIGHT)
// ============================================================================
// Echelle nommee text-body font-semibold — pas de text-kpi font-bold.

import type { LucideIcon } from 'lucide-react';
import { Users, Wallet, Activity, BellRing } from 'lucide-react';
import { useTranslation } from '../../i18n';
import type { TeamPayrollBase } from '../../hooks/useMonEquipe';

function DiscreteKpi({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
}) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-xs dark:border-slate-700 dark:bg-slate-800">
      <div className="shrink-0 rounded-xl bg-slate-50 p-3 dark:bg-slate-900/40">
        <Icon size={22} className="text-slate-600 dark:text-slate-400" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="text-meta font-medium text-slate-500 dark:text-slate-400">{label}</p>
        <p className="mt-0.5 text-body font-semibold tabular-nums text-slate-700 dark:text-slate-200">
          {value}
        </p>
      </div>
    </div>
  );
}

function formatMoney(locale: string, value: number): string {
  return new Intl.NumberFormat(locale.startsWith('en') ? 'en-CA' : 'fr-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

export function AccueilTeamKpis({
  locale,
  headcount,
  submittedCount,
  payroll,
  openReminders,
}: {
  locale: string;
  headcount: number;
  submittedCount: number;
  payroll: TeamPayrollBase | null;
  openReminders: number;
}) {
  const { t } = useTranslation();

  let payrollValue = '—';
  if (payroll) {
    payrollValue = formatMoney(locale, payroll.total);
    if (payroll.unpricedCount > 0) {
      payrollValue = t('gest:accueil.teamView.kpis.payrollWithUnpriced', {
        amount: formatMoney(locale, payroll.total),
        count: payroll.unpricedCount,
      });
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <DiscreteKpi
        icon={Users}
        label={t('gest:monEquipe.kpis.headcount')}
        value={String(headcount)}
      />
      <DiscreteKpi
        icon={Activity}
        label={t('gest:monEquipe.kpis.surveys')}
        value={`${submittedCount}/${headcount}`}
      />
      <DiscreteKpi
        icon={Wallet}
        label={t('gest:monEquipe.kpis.payroll')}
        value={payrollValue}
      />
      <DiscreteKpi
        icon={BellRing}
        label={t('gest:monEquipe.kpis.reminders')}
        value={String(openReminders)}
      />
    </div>
  );
}
